import React, { useRef, useEffect } from "react";
import type { CVData, WorkEntry, EducationEntry } from "@/lib/cvParser";

// ── Editable text primitive ──────────────────────────────────────────────────
interface EditableTextProps {
  value: string;
  onUpdate: (v: string) => void;
  style?: React.CSSProperties;
  multiline?: boolean;
}

const EditableText = ({ value, onUpdate, style, multiline = false }: EditableTextProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value;
    }
  }, [value]);

  const handleBlur = () => {
    if (ref.current) {
      const v = ref.current.innerText.trim();
      if (v !== value) onUpdate(v);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{ outline: "none", cursor: "text", ...style }}
    >
      {value}
    </div>
  );
};

// ── Section heading ─────────────────────────────────────────────────────────
const SectionHeading = ({ children, onUpdate }: { children: string; onUpdate?: (v: string) => void }) => (
  <div style={{ marginBottom: "12px", marginTop: "16px" }}>
    {onUpdate ? (
      <EditableText
        value={children}
        onUpdate={onUpdate}
        style={{
          fontSize: "14px",
          fontWeight: 700,
          color: "#000",
          textTransform: "uppercase",
          letterSpacing: "1px",
          display: "inline-block",
          marginBottom: "4px"
        }}
      />
    ) : (
      <span style={{
        fontSize: "14px",
        fontWeight: 700,
        color: "#000",
        textTransform: "uppercase",
        letterSpacing: "1px",
        display: "inline-block",
        marginBottom: "4px"
      }}>{children}</span>
    )}
    <div style={{ height: "1px", backgroundColor: "#000", width: "100%", opacity: 0.8 }} />
  </div>
);

// ── Debounce helper ───────────────────────────────────────────────────────
function debounce<T extends (...args: any[]) => void>(fn: T, wait: number) {
  let t: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

interface MinimalistTemplateProps {
  data: CVData;
  onUpdate?: (newData: CVData) => void;
}

const MinimalistTemplate = React.forwardRef<HTMLDivElement, MinimalistTemplateProps>(
  ({ data, onUpdate }, ref) => {

    const debouncedUpdate = React.useCallback(
      debounce((newData: CVData) => { if (onUpdate) onUpdate(newData); }, 300),
      [onUpdate]
    );

    const updH = (key: "name" | "jobTitle", val: string) => debouncedUpdate({ ...data, [key]: val });
    const updSection = (key: keyof NonNullable<CVData["sectionNames"]>, val: string) =>
      debouncedUpdate({ ...data, sectionNames: { ...(data.sectionNames || {}), [key]: val } as any });

    const updBasic = (i: number, val: string) => {
      const a = [...data.basicInfo]; a[i] = val; debouncedUpdate({ ...data, basicInfo: a });
    };
    const updProfile = (val: string) => debouncedUpdate({ ...data, profile: val });

    const updWork = (i: number, field: keyof WorkEntry, val: any) => {
      const a = [...data.workExperience]; a[i] = { ...a[i], [field]: val };
      debouncedUpdate({ ...data, workExperience: a });
    };
    const updWorkBullet = (wi: number, bi: number, val: string) => {
      const a = [...data.workExperience];
      const b = [...a[wi].bullets]; b[bi] = val; a[wi] = { ...a[wi], bullets: b };
      debouncedUpdate({ ...data, workExperience: a });
    };
    const updEdu = (i: number, field: keyof EducationEntry, val: any) => {
      const a = [...data.education]; a[i] = { ...a[i], [field]: val };
      debouncedUpdate({ ...data, education: a });
    };

    const sNames = data.sectionNames || {
      basicInfo: "Contact",
      profile: "Professional Summary",
      keySkills: "Highlights of Skills",
      workExperience: "Work Experience",
      education: "Education",
      interests: "Additional Sections",
      references: "References",
    };

    const baseFont = "Arial, Helvetica, sans-serif";

    // Split contact info into left and right columns
    const halfContact = Math.ceil(data.basicInfo.length / 2);
    const leftContact = data.basicInfo.slice(0, halfContact);
    const rightContact = data.basicInfo.slice(halfContact);

    return (
      <div
        ref={ref}
        id="cv-template-root"
        style={{
          backgroundColor: "#ffffff",
          color: "#000",
          fontFamily: baseFont,
          width: "794px",
          minHeight: "1122px",
          boxSizing: "border-box",
          padding: "48px 60px",
          margin: "0 auto",
        }}
      >
        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "inline-block", marginBottom: "12px" }}>
            <EditableText
              value={data.name}
              onUpdate={(v) => updH("name", v)}
              style={{ fontSize: "32px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px" }}
            />
          </div>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontSize: "12px", color: "#000" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {leftContact.map((item, i) => (
                <EditableText
                  key={`left-${i}`}
                  value={item}
                  onUpdate={(v) => updBasic(i, v)}
                />
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "flex-end", textAlign: "right" }}>
              {rightContact.map((item, i) => (
                <EditableText
                  key={`right-${i}`}
                  value={item}
                  onUpdate={(v) => updBasic(i + halfContact, v)}
                />
              ))}
            </div>
          </div>
          
          <div style={{ height: "1px", backgroundColor: "#000", width: "100%", marginTop: "12px", opacity: 0.8 }} />
        </div>

        {/* ── SKILLS ─────────────────────────────────────────────────── */}
        {data.keySkills.length > 0 && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("keySkills", v)}>{sNames.keySkills}</SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "16px" }}>
              {data.keySkills.map((skill, i) => (
                <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <span style={{ fontSize: "12px" }}>•</span>
                  <EditableText
                    value={skill}
                    onUpdate={(v) => {
                      const newSkills = [...data.keySkills];
                      newSkills[i] = v;
                      debouncedUpdate({ ...data, keySkills: newSkills });
                    }}
                    multiline
                    style={{ fontSize: "12px", lineHeight: "1.5", flex: 1 }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── WORK EXPERIENCE ─────────────────────────────────────────── */}
        {data.workExperience.length > 0 && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("workExperience", v)}>{sNames.workExperience}</SectionHeading>
            {data.workExperience.map((entry, i) => (
              <div key={i} style={{ marginBottom: "16px" }} className="minimalist-work-entry">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "2px" }}>
                  <EditableText
                    value={entry.company}
                    onUpdate={(v) => updWork(i, "company", v)}
                    style={{ fontSize: "13px", fontWeight: 700 }}
                  />
                  <EditableText
                    value={entry.dates}
                    onUpdate={(v) => updWork(i, "dates", v)}
                    style={{ fontSize: "12px", fontWeight: 700 }}
                  />
                </div>
                <div style={{ marginBottom: "6px" }}>
                  <EditableText
                    value={entry.jobTitle}
                    onUpdate={(v) => updWork(i, "jobTitle", v)}
                    style={{ fontSize: "13px", fontWeight: 700 }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "16px" }}>
                  {entry.bullets.map((b, bi) => (
                    <div key={bi} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                      <span style={{ fontSize: "12px" }}>•</span>
                      <EditableText
                        value={b}
                        onUpdate={(v) => updWorkBullet(i, bi, v)}
                        multiline
                        style={{ fontSize: "12px", lineHeight: "1.5", flex: 1 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── EDUCATION ─────────────────────────────────────────────── */}
        {data.education.length > 0 && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("education", v)}>{sNames.education}</SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingLeft: "16px" }}>
              {data.education.map((entry, i) => (
                <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <span style={{ fontSize: "12px" }}>•</span>
                  <div style={{ flex: 1, fontSize: "12px", lineHeight: "1.5" }}>
                    <EditableText
                      value={`${entry.qualification} - ${entry.institution}`}
                      onUpdate={(v) => {
                        const parts = v.split(' - ');
                        updEdu(i, "qualification", parts[0] || v);
                        if (parts[1]) updEdu(i, "institution", parts[1]);
                      }}
                      style={{ display: "inline" }}
                    />
                    {entry.dates && (
                      <span>
                        {", "}
                        <EditableText
                          value={entry.dates}
                          onUpdate={(v) => updEdu(i, "dates", v)}
                          style={{ display: "inline" }}
                        />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PROFESSIONAL SUMMARY ────────────────────────────────────── */}
        {data.profile && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("profile", v)}>{sNames.profile}</SectionHeading>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", paddingLeft: "16px" }}>
              <span style={{ fontSize: "12px" }}>•</span>
              <EditableText
                value={data.profile}
                onUpdate={updProfile}
                multiline
                style={{ fontSize: "12px", lineHeight: "1.6", flex: 1 }}
              />
            </div>
          </div>
        )}

        {/* ── INTERESTS ───────────────────────────────────────────────── */}
        {data.interests.length > 0 && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("interests", v)}>{sNames.interests}</SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "16px" }}>
              {data.interests.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <span style={{ fontSize: "12px" }}>•</span>
                  <EditableText
                    value={item}
                    onUpdate={(v) => {
                      const newArr = [...data.interests];
                      newArr[i] = v;
                      debouncedUpdate({ ...data, interests: newArr });
                    }}
                    multiline
                    style={{ fontSize: "12px", flex: 1, lineHeight: "1.5" }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── REFERENCES ─────────────────────────────────────────────────── */}
        {data.references && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("references", v)}>{sNames.references}</SectionHeading>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", paddingLeft: "16px" }}>
              <span style={{ fontSize: "12px" }}>•</span>
              <EditableText
                value={data.references}
                onUpdate={(v) => debouncedUpdate({ ...data, references: v })}
                multiline
                style={{ fontSize: "12px", flex: 1, lineHeight: "1.5" }}
              />
            </div>
          </div>
        )}

      </div>
    );
  }
);

MinimalistTemplate.displayName = "MinimalistTemplate";
(MinimalistTemplate as any).templateName = "Minimalist";

export default MinimalistTemplate;
